import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explainOptional } from '../explain/Explanation'
import DepositPreauthInstructions from '../types/instructions/DepositPreauth'
import TransactionType from '../types/TransactionType'

/**
 * An annotation for a DepositPreauth transaction.
 *
 * @property type - The type of the transaction.
 * @property authorize - An explanation of authorize.
 * @property unauthorize - An explanation of unauthorize.
 */
export interface DepositPreauthAnnotation {
  readonly type: TransactionType.DepositPreauth
  readonly authorize?: Explanation<string>
  readonly unauthorize?: Explanation<string>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: DepositPreauthInstructions,
): DepositPreauthAnnotation {
  const authorize = explainOptional(
    transactionInstructions.Authorize,
    (auth) =>
      `Give account ${cffd(
        auth,
        'auth',
      )} pre-approval to send funds to the sender of this transaction.`,
  )

  const unauthorize = explainOptional(
    transactionInstructions.Unauthorize,
    (unauth) =>
      `Revoke account ${cffd(
        unauth,
        'unauthorize',
      )} 's pre-approval to send funds to the sender of this transaction.`,
  )

  return {
    ...(authorize && { authorize }),
    ...(unauthorize && { unauthorize }),

    type: TransactionType.DepositPreauth,
  }
}
